import { PreviewPhraseData } from './previewPhrases';

type DurationLetter = 'w' | 'h' | 'q' | 'e';

interface PreviewEvent {
  pitch: { step: string; octave: number } | null;
  // null: a rest that fills the whole bar
  duration: DurationLetter | null;
  dotted: boolean;
  hidden: boolean;
  finger: number | null;
}

// Divisions per quarter note in the generated MusicXML.
const DIVISIONS = 4;
const durationDivisions: Record<DurationLetter, number> = { w: 16, h: 8, q: 4, e: 2 };
const durationTypes: Record<DurationLetter, string> = {
  w: 'whole',
  h: 'half',
  q: 'quarter',
  e: 'eighth',
};
const EVENT_PATTERN = /^(-)?(?:([A-G])([0-8])|(r))\/([whqe])(\.)?(?:@([1-5]))?$/;

const parseEvent = (token: string): PreviewEvent => {
  if (token === 'R' || token === '-R') {
    return { pitch: null, duration: null, dotted: false, hidden: token === '-R', finger: null };
  }
  const match = EVENT_PATTERN.exec(token);
  if (!match) throw new Error(`Unreadable preview phrase event "${token}"`);
  const [, hiddenMark, step, octave, rest, duration, dot, finger] = match;
  return {
    pitch: rest ? null : { step, octave: Number(octave) },
    duration: duration as DurationLetter,
    dotted: dot === '.',
    hidden: hiddenMark === '-',
    finger: finger ? Number(finger) : null,
  };
};

const parseBar = (bar: string): PreviewEvent[] => bar.trim().split(/\s+/).map(parseEvent);

const parseTime = (time: string): { beats: number; beatType: number } => {
  const match = /^([1-9][0-9]?)\/(2|4|8)$/.exec(time);
  if (!match) throw new Error(`Unreadable time signature "${time}"`);
  return { beats: Number(match[1]), beatType: Number(match[2]) };
};

const barDivisions = (time: string): number => {
  const { beats, beatType } = parseTime(time);
  return (beats * DIVISIONS * 4) / beatType;
};

const eventDivisions = (event: PreviewEvent, barLength: number): number => {
  if (event.duration === null) return barLength;
  const plain = durationDivisions[event.duration];
  return event.dotted ? plain * 1.5 : plain;
};

const noteXml = (event: PreviewEvent, staff: 1 | 2, barLength: number): string => {
  const voice = staff === 1 ? 1 : 5;
  const visibility = event.hidden ? ' print-object="no"' : '';
  const sound = event.pitch
    ? `<pitch><step>${event.pitch.step}</step><octave>${event.pitch.octave}</octave></pitch>`
    : event.duration === null
      ? '<rest measure="yes"/>'
      : '<rest/>';
  const type = event.duration === null ? '' : `<type>${durationTypes[event.duration]}</type>`;
  const dot = event.dotted ? '<dot/>' : '';
  const fingering =
    event.finger === null
      ? ''
      : `<notations><technical><fingering placement="${staff === 1 ? 'above' : 'below'}">${
          event.finger
        }</fingering></technical></notations>`;
  return (
    `<note${visibility}>${sound}<duration>${eventDivisions(event, barLength)}</duration>` +
    `<voice>${voice}</voice>${type}${dot}<staff>${staff}</staff>${fingering}</note>`
  );
};

const staffXml = (bar: string, staff: 1 | 2, barLength: number, where: string): string => {
  const events = parseBar(bar);
  const total = events.reduce((sum, event) => sum + eventDivisions(event, barLength), 0);
  if (total !== barLength) {
    throw new Error(`${where} lasts ${total} divisions, the bar ${barLength}`);
  }
  return events.map((event) => noteXml(event, staff, barLength)).join('');
};

const firstBarAttributes = (time: string): string => {
  const { beats, beatType } = parseTime(time);
  return (
    `<attributes><divisions>${DIVISIONS}</divisions><key><fifths>0</fifths></key>` +
    `<time><beats>${beats}</beats><beat-type>${beatType}</beat-type></time><staves>2</staves>` +
    '<clef number="1"><sign>G</sign><line>2</line></clef>' +
    '<clef number="2"><sign>F</sign><line>4</line></clef></attributes>'
  );
};

// The phrase as a one-part, two-staff MusicXML score. Throws if a bar does not add up.
export const previewPhraseMusicXml = (phrase: PreviewPhraseData): string => {
  if (phrase.treble.length === 0 || phrase.treble.length !== phrase.bass.length) {
    throw new Error('A preview phrase needs the same number of bars in both staves');
  }
  const barLength = barDivisions(phrase.time);
  const measures = phrase.treble.map((trebleBar, index) => {
    const attributes = index === 0 ? firstBarAttributes(phrase.time) : '';
    const treble = staffXml(trebleBar, 1, barLength, `Treble bar ${index + 1}`);
    const bass = staffXml(phrase.bass[index], 2, barLength, `Bass bar ${index + 1}`);
    return (
      `<measure number="${index + 1}">${attributes}${treble}` +
      `<backup><duration>${barLength}</duration></backup>${bass}</measure>`
    );
  });
  return (
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<score-partwise version="3.1"><part-list><score-part id="P1">' +
    '<part-name print-object="no">Piano</part-name></score-part></part-list>' +
    `<part id="P1">${measures.join('')}</part></score-partwise>`
  );
};

// When a note or rest starts in either staff, in divisions from the start of the bar.
const barOnsets = (bar: string, barLength: number): number[] =>
  parseBar(bar).reduce<{ at: number; onsets: number[] }>(
    (walk, event) => ({
      at: walk.at + eventDivisions(event, barLength),
      onsets: [...walk.onsets, walk.at],
    }),
    { at: 0, onsets: [] },
  ).onsets;

// The engraver's tightest one-line layout, measured in a browser for every level, is close
// to a fixed room (brace, clefs, time signature, bar lines) plus a step for each moment a
// note or rest starts in either staff. These two figures, in staff spaces (the gap between
// two staff lines), sit 5 to 15% above it, so the phrase is never drawn tighter than that.
const OPENING_SPACES = 28;
const SPACES_PER_ONSET = 1.35;

// The narrowest width, in CSS pixels, the phrase can be drawn at with the given staff
// space before its notes crowd; below it the preview is hidden instead.
export const previewPhraseMinimumWidth = (
  phrase: PreviewPhraseData,
  staffSpace: number,
): number => {
  const barLength = barDivisions(phrase.time);
  const onsets = phrase.treble.reduce(
    (sum, trebleBar, index) =>
      sum +
      new Set([...barOnsets(trebleBar, barLength), ...barOnsets(phrase.bass[index], barLength)])
        .size,
    0,
  );
  return Math.ceil((OPENING_SPACES + SPACES_PER_ONSET * onsets) * staffSpace);
};
